// @ts-check
// Galeria de modelos de fluxo (/campaigns/flows/templates; spec 2.0.0, fluxos.md seção 20.3; contrato 09 de fluxos,
// seção 17): categorias na lateral (chips no celular), busca, o cartão "Criar em branco" e um cartão por modelo com
// "Visualizar" (prévia no canvas somente leitura) e "Usar modelo" (nome, depois as mensagens, depois o editor). Os
// botões ficam sempre visíveis, por acessibilidade e toque.
import { html } from 'htm/preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { createFlow, listFlowTemplates, listFlows } from '../../data/flows.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { flowCollections, flowEditorHref } from '../../lib/flow-list.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowDialog } from './flow-dialog.js';
import { FlowMiniCanvas, miniCanvasLabel } from './flow-mini-canvas.js';
import { FlowNameDialog } from './flow-name-dialog.js';
import { FlowTemplateChoicesDialog } from './flow-template-choices-dialog.js';
import { useFlowLookupData, useSummaryLookups } from './flow-lookups.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/flows.js').FlowTemplateItem} FlowTemplateItem
 * @typedef {import('../../lib/flow-list.js').FlowSummary} FlowSummary
 * @typedef {{ step: 'preview' | 'name' | 'choices', template: FlowTemplateItem | null, name?: string, collection?: string | null } | null} GalleryDialog
 */

export const FLOW_TEMPLATE_CATEGORIES = Object.freeze(['ALL', 'CAPTURE', 'VISIT', 'ENROLLMENT', 'RELATIONSHIP']);
const TEMPLATE_CATEGORY_KEYS = /** @type {Record<string, string>} */ ({ CAPTURE: 'LEAD', VISIT: 'PATH', ENROLLMENT: 'LEAD', RELATIONSHIP: 'COMMUNICATION' });

/** Nome sugerido: o do modelo e a data de hoje ("Boas-vindas ao lead 10/10/2026"). @param {FlowTemplateItem} template @param {Date} [now] */
export function suggestedFlowName(template, now = new Date()) {
  const date = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Sao_Paulo' }).format(now);
  return `${t(template.nameKey)} ${date}`;
}

/**
 * Modelos de uma categoria que batem com a busca (nome e descrição, sem acento).
 * @param {FlowTemplateItem[]} templates @param {string} category @param {string} search
 */
export function filterFlowTemplates(templates, category, search) {
  const needle = normalizeSearchText(search);
  return templates.filter((template) => (category === 'ALL' || template.category === category)
    && (!needle || normalizeSearchText(`${t(template.nameKey)} ${t(template.descriptionKey)}`).includes(needle)));
}

export function FlowTemplatesPage() {
  const [templates, setTemplates] = useState(/** @type {FlowTemplateItem[] | null} */ (null));
  const [flows, setFlows] = useState(/** @type {FlowSummary[]} */ ([]));
  const [loadError, setLoadError] = useState(/** @type {unknown} */ (null));
  const [category, setCategory] = useState('ALL');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(/** @type {GalleryDialog} */ (null));
  const lookupData = useFlowLookupData({ includeFlows: false });

  const load = () => {
    setLoadError(null);
    listFlowTemplates().then(setTemplates).catch((error) => setLoadError(error));
    listFlows().then((result) => setFlows(result.flows)).catch(() => setFlows([]));
  };
  useEffect(() => {
    document.title = t('flows.templates.title');
    load();
  }, []);

  const visible = useMemo(() => (templates ? filterFlowTemplates(templates, category, search) : []), [templates, category, search]);
  /** @param {{ name: string, collection: string | null, templateKey?: string, choices?: Record<string, unknown> }} input */
  const create = async (input) => {
    try {
      const flow = await createFlow(input);
      navigate(flowEditorHref(flow.id));
      return true;
    } catch (error) {
      showErrorSnackbar(error);
      return false;
    }
  };

  return html`<${PageCard} className="flows-page flow-templates-page"
    header=${html`<${PageHeader} icon="IconSitemap" title=${t('flows.templates.title')}
      breadcrumb=${html`<button type="button" class="flow-back-link" onClick=${() => navigate('/campaigns/flows')}><${Icon} name="IconChevronLeft" size=${16} /><span>${t('flows.list.title')}</span></button>`} />`}>
    <div class="flow-templates" data-testid="flow-templates-page">
      <p class="flow-templates-subtitle">${t('flows.templates.subtitle')}</p>
      <div class="flow-templates-layout">
        <nav class="flow-templates-sidebar" aria-label=${t('flows.templates.categoriesLabel')}>
          <label class="flows-search flow-templates-search">
            <${Icon} name="IconSearch" size=${16} />
            <input type="search" class="flows-search-input" value=${search} placeholder=${t('flows.templates.search')} aria-label=${t('flows.templates.search')}
              onInput=${(/** @type {InputEvent} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
          <ul class="flow-templates-categories" role="list">
            ${FLOW_TEMPLATE_CATEGORIES.map((key) => html`<li key=${key}>
              <button type="button" class="flow-templates-category" aria-pressed=${category === key ? 'true' : 'false'} onClick=${() => setCategory(key)}>
                ${t(`flows.templates.categories.${key}`)}
              </button>
            </li>`)}
          </ul>
        </nav>
        <div class="flow-templates-content">
          ${loadError
            ? html`<${EmptyState} icon="IconAlertTriangle" title=${t('flows.templates.loadError')}
                action=${html`<${Button} title=${t('flows.common.retry')} variant="secondary" size="small" onClick=${load} />`} />`
            : html`<ul class="flow-templates-grid" role="list">
                ${category === 'ALL' && !search
                  ? html`<li class="flow-template-card flow-template-blank">
                      <span class="flow-template-blank-icon" aria-hidden="true"><${Icon} name="IconPlus" size=${28} /></span>
                      <h3 class="flow-template-name">${t('flows.templates.blankTitle')}</h3>
                      <p class="flow-template-description">${t('flows.templates.blankText')}</p>
                      <div class="flow-template-actions">
                        <${Button} title=${t('flows.templates.select')} accent="blue" size="small" onClick=${() => setDialog({ step: 'name', template: null })} />
                      </div>
                    </li>`
                  : null}
                ${templates === null
                  ? Array.from({ length: 3 }, (_, index) => html`<li key=${`skeleton-${index}`} class="flow-template-card"><${Skeleton} height=${140} radius=${8} /></li>`)
                  : visible.map((template) => html`<${FlowTemplateCard} key=${template.key} template=${template}
                      onPreview=${() => setDialog({ step: 'preview', template })} onUse=${() => setDialog({ step: 'name', template })} />`)}
              </ul>
              ${templates && visible.length === 0 && search
                ? html`<${EmptyState} icon="IconSearch" title=${t('flows.templates.noResults', { query: search })} />`
                : null}`}
        </div>
      </div>
    </div>
    ${dialog?.step === 'preview' && dialog.template
      ? html`<${FlowTemplatePreviewDialog} template=${dialog.template} lookupData=${lookupData} onClose=${() => setDialog(null)}
          onUse=${() => setDialog({ step: 'name', template: dialog.template })} />`
      : null}
    ${dialog?.step === 'name'
      ? html`<${FlowNameDialog} mode="create" initialName=${dialog.name ?? (dialog.template ? suggestedFlowName(dialog.template) : '')} initialCollection=${dialog.collection ?? null}
          collections=${flowCollections(flows)} existingNames=${flows.map((flow) => flow.name)} onClose=${() => setDialog(null)}
          onSubmit=${(/** @type {{ name: string, collection: string | null }} */ value) => {
            if (!dialog.template) return create(value);
            if (dialog.template.choices.length === 0) return create({ ...value, templateKey: dialog.template.key, choices: {} });
            setDialog({ step: 'choices', template: dialog.template, name: value.name, collection: value.collection });
            return true;
          }} />`
      : null}
    ${dialog?.step === 'choices' && dialog.template
      ? html`<${FlowTemplateChoicesDialog} template=${dialog.template} whatsappTemplates=${lookupData.whatsappTemplates} channels=${lookupData.channels}
          onClose=${() => setDialog(null)} onBack=${() => setDialog({ ...dialog, step: 'name' })}
          onCreate=${(/** @type {Record<string, unknown>} */ choices) => create({
            name: dialog.name ?? '', collection: dialog.collection ?? null, templateKey: /** @type {FlowTemplateItem} */ (dialog.template).key, choices,
          })} />`
      : null}
  <//>`;
}

/**
 * Cartão de um modelo: ícone, nome, descrição, canais e os dois botões.
 * @param {{ template: FlowTemplateItem, onPreview: () => void, onUse: () => void }} props
 */
function FlowTemplateCard({ template, onPreview, onUse }) {
  const colorCategory = TEMPLATE_CATEGORY_KEYS[template.category] ?? 'COMMUNICATION';
  return html`<li class="flow-template-card" data-testid="flow-template-card">
    <span class="flow-template-icon" data-category=${colorCategory} aria-hidden="true"><${Icon} name=${template.icon} size=${20} /></span>
    <span class="flow-template-category">${t(`flows.templates.categories.${template.category}`)}</span>
    <h3 class="flow-template-name">${t(template.nameKey)}</h3>
    <p class="flow-template-description">${t(template.descriptionKey)}</p>
    <div class="flow-template-channels">
      ${template.channels.map((channel) => html`<span key=${channel} class="flow-template-channel">
        <${Icon} name=${channel === 'EMAIL' ? 'IconMail' : 'IconBrandWhatsapp'} size=${12} />${t(`flows.list.channels.${channel}`)}
      </span>`)}
    </div>
    <div class="flow-template-actions">
      <${Button} title=${t('flows.templates.preview')} variant="secondary" size="small" onClick=${onPreview} ariaLabel=${t('flows.templates.previewOf', { name: t(template.nameKey) })} />
      <${Button} title=${t('flows.templates.use')} accent="blue" size="small" onClick=${onUse} ariaLabel=${t('flows.templates.useOf', { name: t(template.nameKey) })} />
    </div>
  </li>`;
}

/**
 * Prévia do modelo: descrição e o canvas somente leitura (60%).
 * @param {{ template: FlowTemplateItem, lookupData: import('./flow-lookups.js').FlowLookupData, onClose: () => void, onUse: () => void }} props
 */
function FlowTemplatePreviewDialog({ template, lookupData, onClose, onUse }) {
  const lookups = useSummaryLookups(lookupData, template.definition);
  const name = t(template.nameKey);
  return html`<${FlowDialog} title=${name} size="wide" onClose=${onClose} testId="flow-template-preview"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.templates.use')} accent="blue" size="small" onClick=${onUse} />`}>
    <h3 class="flow-dialog-section-title">${t('flows.templates.description')}</h3>
    <p class="flow-dialog-text">${t(template.descriptionKey)}</p>
    <h3 class="flow-dialog-section-title">${t('flows.templates.previewTitle')}</h3>
    <div class="flow-template-preview-canvas">
      <${FlowMiniCanvas} definition=${template.definition} lookups=${lookups} ariaLabel=${miniCanvasLabel(name)} initialZoom=${0.6} />
    </div>
  <//>`;
}
